/**
 * Products — catalogue de produits / prestations (Ventes).
 * Auto-populé depuis les factures + saisie manuelle + import Excel intelligent.
 */
import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import {
  Search, Package, Plus, Upload, Pencil, Trash2, FileText,
  TrendingUp, RefreshCw, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import SmartImportModal from "@/components/SmartImportModal";

const fmtEUR = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

const CATEGORIES = ["SaaS", "Prestation", "Matériel", "Location", "Maintenance", "Autre"];
const SOURCES = [
  { value: "manual", label: "Manuel" },
  { value: "auto_invoice", label: "Auto (facture)" },
  { value: "axonaut", label: "Axonaut" },
  { value: "pennylane", label: "Pennylane" },
  { value: "iopole", label: "IOPOLE" },
];

const CATEGORY_STYLE = {
  "SaaS":         "bg-blue-50 text-blue-700 border-blue-200",
  "Prestation":   "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Matériel":     "bg-amber-50 text-amber-700 border-amber-200",
  "Location":     "bg-violet-50 text-violet-700 border-violet-200",
  "Maintenance":  "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Autre":        "bg-slate-50 text-slate-600 border-slate-200",
};

function ProductEditor({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", unit_price_ht: "", vat_rate: 20,
    unit: "u", sku: "", category: "Autre", tags: [],
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!product;

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "",
        description: product.description || "",
        unit_price_ht: product.unit_price_ht ?? "",
        vat_rate: product.vat_rate ?? 20,
        unit: product.unit || "u",
        sku: product.sku || "",
        category: product.category || "Autre",
        tags: product.tags || [],
      });
    } else {
      setForm({ name: "", description: "", unit_price_ht: "", vat_rate: 20,
                 unit: "u", sku: "", category: "Autre", tags: [] });
    }
  }, [product, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_price_ht: Number(form.unit_price_ht) || 0,
        vat_rate: Number(form.vat_rate) || 20,
      };
      if (isEdit) await api.patch(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(isEdit ? "Produit modifié" : "Produit créé");
      onSaved?.(); onOpenChange(false);
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="product-editor">
        <DialogHeader><DialogTitle>{isEdit ? "Modifier le produit" : "Nouveau produit"}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label htmlFor="product-name">Nom *</Label>
            <Input id="product-name" name="product-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    autoFocus data-testid="product-name-input" />
          </div>
          <div><Label htmlFor="product-description">Description</Label>
            <Input id="product-description" name="product-description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label htmlFor="product-price">Prix HT (€)</Label>
              <Input id="product-price" name="product-price" type="number" step="0.01" value={form.unit_price_ht}
                      onChange={(e) => setForm((p) => ({ ...p, unit_price_ht: e.target.value }))} />
            </div>
            <div><Label htmlFor="product-vat">TVA (%)</Label>
              <Input id="product-vat" name="product-vat" type="number" step="0.1" value={form.vat_rate}
                      onChange={(e) => setForm((p) => ({ ...p, vat_rate: e.target.value }))} />
            </div>
            <div><Label htmlFor="product-unit">Unité</Label>
              <Input id="product-unit" name="product-unit" value={form.unit} onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
                      placeholder="u, h, j, forfait..." />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="product-sku">Référence produit <span className="text-slate-400 font-normal">(optionnel)</span></Label>
              <Input id="product-sku" name="product-sku" value={form.sku} onChange={(e) => setForm((p) => ({ ...p, sku: e.target.value }))}
                     placeholder="Ex: PROD-001, ABON-SAAS, LIC-2026…" />
              <p className="text-[11px] text-slate-400 mt-1">
                Code interne pour retrouver ce produit rapidement (facultatif).
              </p>
            </div>
            <div><Label htmlFor="product-category">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger id="product-category" name="product-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="product-save-btn">
              {saving ? "Enregistrement..." : (isEdit ? "Enregistrer" : "Créer")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [source, setSource] = useState("all");
  const [sort, setSort] = useState("usage");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const runBackfill = async () => {
    if (!window.confirm("Analyser toutes les factures existantes et créer les produits manquants dans le catalogue ?")) return;
    setBackfilling(true);
    try {
      const r = await api.post("/products/backfill");
      toast.success(`${r.data.products_touched} produit(s) mis à jour depuis ${r.data.invoices_processed} facture(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBackfilling(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.search = q;
      if (category !== "all") params.category = category;
      if (source !== "all") params.source = source;
      const r = await api.get("/products", { params });
      setProducts(r.data.products || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, source, sort]);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t); /* eslint-disable-next-line */
  }, [q]);

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le produit "${p.name}" ?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Produit supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const totalUsage = useMemo(() => products.reduce((s, p) => s + (p.usage_count || 0), 0), [products]);

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Catalogue Produits
          </h1>
          <p className="text-sm text-slate-500">
            {products.length} produit{products.length > 1 ? "s" : ""} · {totalUsage} utilisation{totalUsage > 1 ? "s" : ""}
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={runBackfill} disabled={backfilling}
                     data-testid="products-backfill-btn">
              {backfilling ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />}
              Extraire depuis mes factures
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="products-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importer Excel
            </Button>
            <Button onClick={() => { setEditing(null); setEditorOpen(true); }}
                     className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="products-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau produit
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="products-search" name="products-search" value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder="Rechercher nom, référence, description..." className="pl-10"
                      data-testid="products-search" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="products-category" name="products-category" data-testid="products-category-filter"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger id="products-source" name="products-source"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes sources</SelectItem>
                {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Trier par :</span>
            {[["usage", "Utilisation"], ["name", "Nom A→Z"], ["recent", "Récent"]].map(([v, l]) => (
              <button key={v} onClick={() => setSort(v)}
                       className={`px-2 py-1 rounded transition ${sort === v ? "bg-blue-100 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-100"}`}>
                {l}
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={load} className="ml-auto">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(8)].map((_, i) => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center" data-testid="products-empty">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun produit encore.</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">
                Si vous avez déjà des factures importées, extrayez-les en 1 clic ↓
              </p>
              {canWrite && (
                <Button onClick={runBackfill} disabled={backfilling}
                         className="bg-purple-600 hover:bg-purple-700 text-white"
                         data-testid="products-backfill-empty-btn">
                  {backfilling ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                  Extraire depuis mes factures existantes
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Nom</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Catégorie</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Prix HT</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">TVA</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Unité</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Source</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Utilisation</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p, i) => (
                  <TableRow key={p.id} data-testid={`product-row-${i}`} className="hover:bg-slate-50">
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{p.name}</p>
                        {p.sku && <p className="text-xs text-slate-400 font-mono">{p.sku}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${CATEGORY_STYLE[p.category] || CATEGORY_STYLE.Autre}`}>
                        {p.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmtEUR(p.unit_price_ht)}</TableCell>
                    <TableCell className="text-right font-mono">{p.vat_rate}%</TableCell>
                    <TableCell className="text-sm">/{p.unit || "u"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {SOURCES.find((s) => s.value === p.source)?.label || p.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 text-sm font-mono">
                        {p.usage_count > 0 && <TrendingUp className="w-3 h-3 text-emerald-600" />}
                        {p.usage_count}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setEditorOpen(true); }}
                                   data-testid={`product-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(p)} className="text-red-600 hover:text-red-700"
                                   data-testid={`product-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ProductEditor open={editorOpen} onOpenChange={setEditorOpen} product={editing} onSaved={load} />
      <SmartImportModal isOpen={importOpen} onClose={() => setImportOpen(false)}
                          onSuccess={() => load()} kind="products" />
    </div>
  );
}
